import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, ShieldCheck, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function RolesTab() {
  const [roles, setRoles] = useState(null);
  const [perms, setPerms] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const [r, p] = await Promise.all([api.get("/admin/roles"), api.get("/permissions")]); setRoles(r.data); setPerms(p.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load roles"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { name: edit.name, description: edit.description || null, permissions: edit.permissions };
      if (edit.id) await api.put(`/admin/roles/${edit.id}`, body); else await api.post("/admin/roles", body);
      toast.success(edit.id ? "Role updated" : "Role created"); setEdit(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"? It will be removed from ${r.member_count} user(s).`)) return;
    try { await api.delete(`/admin/roles/${r.id}`); toast.success("Role deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggle = (k) => setEdit(e => ({ ...e, permissions: e.permissions.includes(k) ? e.permissions.filter(x => x !== k) : [...e.permissions, k] }));

  if (!roles) return <div className="opacity-50 font-display tracking-widest">LOADING…</div>;
  return (
    <div className="space-y-5" data-testid="roles-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display font-black text-2xl">Roles & permissions</h2>
          <p className="opacity-60 text-sm mt-1">Roles are bundles of permissions. Assign any number of roles to a user from Admin → Users. Admins always hold every permission. The former tick-boxes are now built-in roles.</p>
        </div>
        <button type="button" onClick={() => setEdit({ name: "", description: "", permissions: [] })} className="btn-volt px-4 py-2 rounded-md text-xs tracking-widest uppercase inline-flex items-center gap-1.5" data-testid="role-new"><Plus className="w-3.5 h-3.5" /> New role</button>
      </div>

      {edit && (
        <div className="card-tactical rounded-md p-5 border-[#83B68B]/40" data-testid="role-editor">
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-[10px] tracking-widest uppercase opacity-60">Role name</Label><Input value={edit.name} disabled={edit.builtin} onChange={e => setEdit({ ...edit, name: e.target.value })} className="mt-1" data-testid="role-name" placeholder="e.g. Fixtures Secretary" /></div>
            <div><Label className="text-[10px] tracking-widest uppercase opacity-60">Description</Label><Input value={edit.description || ""} onChange={e => setEdit({ ...edit, description: e.target.value })} className="mt-1" data-testid="role-description" /></div>
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-1.5">
            {perms.map(p => (
              <label key={p.key} className={`flex items-start gap-2 p-2 rounded border cursor-pointer text-sm ${edit.permissions.includes(p.key) ? "border-[#83B68B]/60 bg-[#83B68B]/10" : "border-border hover:border-white/30"}`} data-testid={`role-perm-${p.key}`}>
                <input type="checkbox" checked={edit.permissions.includes(p.key)} onChange={() => toggle(p.key)} className="mt-0.5 accent-[#83B68B]" />
                <span><span className="font-medium">{p.name}</span><span className="block text-[11px] opacity-60">{p.description}</span></span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => setEdit(null)} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border">Cancel</button>
            <button type="button" onClick={save} disabled={busy || edit.name.trim().length < 2} className="btn-volt px-4 py-2 rounded-md text-[10px] tracking-widest uppercase inline-flex items-center gap-1 disabled:opacity-40" data-testid="role-save"><Check className="w-3 h-3" /> {busy ? "Saving…" : "Save role"}</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {roles.map(r => (
          <div key={r.id} className="card-tactical rounded-md p-4" data-testid={`role-card-${r.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display font-bold text-base inline-flex items-center gap-2">{r.builtin && <ShieldCheck className="w-4 h-4 text-[#83B68B]" />}{r.name}</div>
                <div className="text-[11px] opacity-60 mt-0.5">{r.description}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[10px] tracking-widest uppercase opacity-60 inline-flex items-center gap-1 mr-2" data-testid={`role-members-${r.key}`}><Users className="w-3 h-3" /> {r.member_count}</span>
                <button type="button" onClick={() => setEdit({ ...r, description: r.description || "", permissions: [...(r.permissions || [])] })} className="p-1.5 rounded hover:text-[#83B68B]" title="Edit" data-testid={`role-edit-${r.key}`}><Pencil className="w-3.5 h-3.5" /></button>
                {!r.builtin && <button type="button" onClick={() => remove(r)} className="p-1.5 rounded hover:text-red-400" title="Delete" data-testid={`role-delete-${r.key}`}><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {(r.permissions || []).length === 0 && <span className="text-xs opacity-40">No permissions</span>}
              {(r.permissions || []).map(k => <span key={k} className="text-[10px] px-2 py-0.5 rounded-full border border-[#83B68B]/40 text-[#83B68B]">{perms.find(p => p.key === k)?.name || k}</span>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
